import React, { ReactNode, useId } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ITableHeadings, ITableMetadata } from '../../common/interfaces/common.interface';
import { SortOrderEnum } from '../../common/enums/common.enum';
import { ReactComponent as ListEmptyStateIcon } from '../../assets/list-empty-state-icon.svg';
import { useTranslation } from 'react-i18next';

import { palette } from '../../theme';
/** Page sizes offered; the table's current size is added if a screen uses another. */
export const PAGE_SIZES = [25, 50, 100];

/**
 * For cells holding an amount: right-aligned with figures of equal width, so a column
 * of numbers lines up on its decimal comma and can be read top to bottom.
 */
export const NUMERIC_CELL_SX = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

const BRAND = palette.primary.main;

const BasicTable: React.FC<{
  headings: ITableHeadings[];
  loader: boolean;
  children: ReactNode;
  metadata: ITableMetadata;
  setMetadata: React.Dispatch<React.SetStateAction<ITableMetadata>>;
  sortBy?: string | null;
  sortOrder?: SortOrderEnum;
  /**
   * Called with the column and the direction it should now have. A click on the sorted
   * column flips its direction; a click on another column sorts it ascending. There is no
   * "unsorted" state: a table always has an order, and a third click that silently falls
   * back to some server default reads as the table ignoring the click.
   */
  onSortChange?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  /**
   * Shown instead of the empty-list illustration, for a list that is empty because of a
   * filter: "no results" there reads as "nothing exists", when the truth is "nothing matches".
   */
  emptyState?: ReactNode;
}> = ({ headings, children, loader, metadata, setMetadata, sortBy, sortOrder, onSortChange, emptyState }) => {
  const { t } = useTranslation();
  const pageSizeLabelId = useId();

  const setPage = (page: number) => setMetadata({ ...metadata, currentPage: page });
  const setPageSize = (itemsPerPage: number) => setMetadata({ ...metadata, itemsPerPage, currentPage: 1 });

  const handleSortClick = (key?: string) => {
    if (!key || !onSortChange) return;
    if (key === sortBy) {
      onSortChange(key, sortOrder === SortOrderEnum.ASC ? 'desc' : 'asc');
    } else {
      onSortChange(key, 'asc');
    }
  };

  const from = metadata.totalItems > 0 ? (metadata.currentPage - 1) * metadata.itemsPerPage + 1 : 0;
  const to = Math.min(metadata.totalItems, metadata.itemsPerPage * metadata.currentPage);
  const lastPage = Math.max(1, Math.ceil(metadata.totalItems / metadata.itemsPerPage));
  const pageSizes = PAGE_SIZES.includes(metadata.itemsPerPage)
    ? PAGE_SIZES
    : [...PAGE_SIZES, metadata.itemsPerPage].sort((a, b) => a - b);

  return (
    <>
      {/* visible overflow on wide screens, so the sticky header sticks to the page that
          scrolls rather than to a container that never does; narrow screens need the
          horizontal scroll more than they need the sticky header */}
      <TableContainer sx={{ overflowX: { xs: 'auto', lg: 'visible' } }}>
        <Table sx={{ minWidth: 650 }} aria-label={t('table.ariaLabel')}>
          <TableHead>
            <TableRow sx={{ padding: 1 }}>
              {headings.map(({ id, label, sortable, sortKey, width, align, content }) => {
                const isActive = !!sortable && !!sortKey && sortKey === sortBy;
                const ariaSort = !sortable ? undefined : isActive ? (sortOrder === SortOrderEnum.DESC ? 'descending' : 'ascending') : 'none';
                return (
                  <TableCell
                    key={id}
                    aria-sort={ariaSort}
                    align={align}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      userSelect: 'none',
                      fontWeight: isActive ? 600 : 'normal',
                      color: isActive ? BRAND : 'text.primary',
                      backgroundColor: palette.surface.subtle,
                      width: width ?? 'auto',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      '&:hover .sort-arrow': { opacity: 1 }
                    }}
                  >
                    {content ? (
                      content
                    ) : sortable && sortKey ? (
                      <ButtonBase
                        onClick={() => handleSortClick(sortKey)}
                        sx={{
                          font: 'inherit',
                          color: 'inherit',
                          gap: 0.5,
                          borderRadius: 1,
                          flexDirection: align === 'right' ? 'row-reverse' : 'row',
                          '&:focus-visible .sort-arrow': { opacity: 1 }
                        }}
                      >
                        {/* headings carry translation keys, so every table translates in one place */}
                        <span>{t(label)}</span>
                        <ArrowUpwardIcon
                          className="sort-arrow"
                          aria-hidden
                          sx={{
                            fontSize: 16,
                            color: isActive ? BRAND : palette.text.placeholder,
                            opacity: isActive ? 1 : 0,
                            transform: isActive && sortOrder === SortOrderEnum.DESC ? 'rotate(180deg)' : 'none',
                            transition: 'opacity 120ms'
                          }}
                        />
                      </ButtonBase>
                    ) : (
                      <span>{label ? t(label) : ''}</span>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loader ? (
              <TableRow sx={{ minHeight: '225px' }}>
                <TableCell colSpan={headings.length} sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : metadata.totalItems === 0 && emptyState ? (
              <TableRow>
                <TableCell colSpan={headings.length} sx={{ textAlign: 'center', py: 6 }}>
                  {emptyState}
                </TableCell>
              </TableRow>
            ) : metadata.totalItems === 0 ? (
              <TableRow>
                <TableCell colSpan={headings.length} sx={{ textAlign: 'center', py: 6 }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
                    <ListEmptyStateIcon style={{ fontSize: 40, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {t('table.noResults')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              children
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {metadata.totalItems > 0 && (
        <Stack
          component="nav"
          aria-label={t('table.pagination')}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ px: 2, py: 1.5, borderTop: `1px solid ${palette.line}` }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography component="label" id={pageSizeLabelId} sx={{ fontSize: 14, color: palette.text.secondary }}>
              {t('table.perPage')}
            </Typography>
            <Select
              size="small"
              labelId={pageSizeLabelId}
              value={metadata.itemsPerPage}
              onChange={event => setPageSize(Number(event.target.value))}
              sx={{ borderRadius: '10px', fontSize: 14, '& .MuiSelect-select': { py: 0.75 } }}
            >
              {pageSizes.map(size => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 14, color: palette.text.secondary, fontVariantNumeric: 'tabular-nums', mr: 1 }}>
              {t('table.range', { from, to, count: metadata.totalItems })}
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              disabled={metadata.currentPage <= 1}
              onClick={() => setPage(metadata.currentPage - 1)}
              sx={{ textTransform: 'none', borderRadius: '10px', borderColor: palette.field.border }}
            >
              {t('table.previous')}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              disabled={metadata.currentPage >= lastPage}
              onClick={() => setPage(metadata.currentPage + 1)}
              sx={{ textTransform: 'none', borderRadius: '10px', borderColor: palette.field.border }}
            >
              {t('table.next')}
            </Button>
          </Stack>
        </Stack>
      )}
    </>
  );
};
export default BasicTable;
